import type { AuthStore } from '@stores/auth/interfaces'
import type { LoginResponse } from '@ts-auth/interfaces'
import type { AxiosError } from 'axios'

import routes from '@config/routes'

import { _applyTokens } from './apply-tokens'
import { _clearTokens } from './clear-tokens'
import { _hydrateFromStorage } from './hydrate-from-storage'

const isRejected = (err: unknown): boolean => {
  const status = (err as AxiosError)?.response?.status
  return status === 401 || status === 403
}

const requestTokens = async (state: AuthStore): Promise<string> => {
  const { $axios } = useNuxtApp()
  const { data } = await $axios.post<LoginResponse>(routes['admin.auth.refresh'], {})

  if (!data?.success) throw new Error(data?.message || 'Refresh failed')

  _applyTokens(state, data.data)

  return state.accessToken
}

export const refreshTokens = async (state: AuthStore): Promise<string | void> => {
  if (state.refreshing) return state.refreshing

  const staleAccess = state.accessToken
  _hydrateFromStorage(state)

  if (state.accessToken && state.accessToken !== staleAccess && state.expiresAt > Date.now()) {
    return state.accessToken
  }

  if (!state.refreshToken) return

  state.refreshing = (async () => {
    const sent = state.refreshToken

    try {
      return await requestTokens(state)
    } catch (err) {
      if (!isRejected(err)) return

      _hydrateFromStorage(state)

      if (state.refreshToken && state.refreshToken !== sent) {
        try {
          return await requestTokens(state)
        } catch (retryErr) {
          if (!isRejected(retryErr)) return
        }
      }

      _clearTokens(state)
      return
    } finally {
      state.refreshing = null
    }
  })()

  return state.refreshing
}
